import {
    Component,
    OnInit,
    AfterViewInit,
    ViewChild,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Angular Material imports
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { LoggerService } from '../../../core/services/logger.service';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute } from '@angular/router';
import {
    LiquidacionesStateService,
    LiquidacionesState,
} from '../../../services/liquidaciones-state.service';
import { LiquidacionesNavigationService } from '../../../services/liquidaciones-navigation.service';
import {
    LiquidacionesService,
    LiquidacionAgenciaFilter,
    LiquidacionAgencia as LiquidacionAgenciaService,
    TemporadaDto as TemporadaDtoService,
} from '../../../services/liquidaciones.service';
import {
    construirFiltrosLiquidaciones,
    EstadoFiltrosLiquidaciones,
    buildFacturacionRequestBody,
    puedeVerBotonFacturacion,
} from './liquidaciones-agencia-filtros.util';
import {
    buildFacturacionWorkbook,
    buildFacturacionFilename,
} from './liquidacion-agencia-facturacion-excel.util';
import { UserService, Client } from '../../../services/user.service';
import { AuthService, User } from '../../../services/auth.service';
import { ClientService } from '../../../services/client.service';
import { ParameterService } from '../../../services/parameter.service';
import { catchError, finalize, startWith, map } from 'rxjs/operators';
import { of, Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { filtrarClientes } from './liquidaciones-agencia-clientes-filter.util';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { FormControl } from '@angular/forms';
import { getDefaultDateRange } from '../../../core/utils/date.utils';
import {
    aplicarEstilosAhojaExcel as aplicarEstilosAhojaExcelUtil,
    capitalizeHeader as capitalizeHeaderUtil,
    filtrarColumnasConServiciosPresentes as filtrarColumnasConServiciosPresentesUtil,
    getColumnWidth as getColumnWidthUtil,
} from './liquidaciones-agencia-excel-estilos.util';
import { LiquidacionesAgenciaExportService } from './liquidaciones-agencia-export.service';

export interface LiquidacionAgenciaLocal {
    id: number;
    numeroLiquidacion: string;
    /**
     * Punto B (OPB, ago-2026). El backend los resuelve desde
     * `tblShippingLiquidationCustomerInvoices` y ya venían en la respuesta del listado,
     * pero no estaban declarados acá — por eso el Excel podía escribirlos sin que el
     * compilador dijera nada. Opcionales porque una liquidación puede no tener factura
     * ni nota de cobro emitidas.
     */
    nroFactura?: string;
    nroNotaCobro?: string;
    cliente: string;
    mesAnio: string;
    nave: string;
    navesTooltip: string;
    tipo: string;
    cntr: number;
    plts: number;
    agenciamiento: number;
    transporte: number;
    gate: number;
    /** MEJ-02: Gate Out, TODO el gate (Venta + Traspaso). Informativo. */
    gateOut?: number;
    /** MEJ-02: Gate In, TODO el gate (Venta + Traspaso). Informativo. */
    gateIn?: number;
    /**
     * Porción del gate que SÍ está dentro de osVenta.
     * ⚠️ Restar `gate` en vez de esto da OS Venta negativa cuando el gate es
     * Traspaso (liq 1696, reportada por OPB el 2026-09-01).
     */
    gateVenta?: number;
    /** Porción Venta del Gate Out. Ver `gateVenta`. */
    gateOutVenta?: number;
    /** Porción Venta del Gate In. Ver `gateVenta`. */
    gateInVenta?: number;
    osVenta: number;
    totalVentaNeto: number;
    totalNeto: number;
    osTraspaso: number;
    /** MEJ-02 (D8): osTraspaso sin el gate. Reemplaza a Gate Out/In en la Cabecera. */
    osTraspasoSinGate?: number;
    total: number;
    estado: string;
    agente: string;
    fechaCreacion: string;
}

export interface LiquidacionAgencia {
    id: number;
    numeroLiquidacion: string;
    cliente: string;
    mesAnio: string;
    nave: string;
    navesTooltip: string;
    tipo: string;
    cntr: number;
    plts: number;
    agenciamiento: number;
    transporte: number;
    gate: number;
    /** MEJ-02: Gate Out, TODO el gate (Venta + Traspaso). Informativo. */
    gateOut?: number;
    /** MEJ-02: Gate In, TODO el gate (Venta + Traspaso). Informativo. */
    gateIn?: number;
    /**
     * Porción del gate que SÍ está dentro de osVenta.
     * ⚠️ Restar `gate` en vez de esto da OS Venta negativa cuando el gate es
     * Traspaso (liq 1696, reportada por OPB el 2026-09-01).
     */
    gateVenta?: number;
    /** Porción Venta del Gate Out. Ver `gateVenta`. */
    gateOutVenta?: number;
    /** Porción Venta del Gate In. Ver `gateVenta`. */
    gateInVenta?: number;
    osVenta: number;
    totalVentaNeto: number;
    totalNeto: number;
    osTraspaso: number;
    total: number;
    estado: string;
    agente: string;
    fechaCreacion: string;
    fechaLiquidacion: string;
}

export interface TemporadaDto {
    id: number;
    anoInicio: number;
    anoFin: number;
    fechaInicio: string;
    fechaFin: string;
    activa: number;
}

export interface LiquidacionAgenciaResponse {
    success: boolean;
    data: {
        items: LiquidacionAgencia[];
        total: number;
    };
}

export interface LiquidacionAgenciaTotales {
    cntr: number;
    plts: number;
    agenciamiento: number;
    transporte: number;
    gate: number;
    /** MEJ-02: Gate Out, TODO el gate (Venta + Traspaso). Informativo. */
    gateOut?: number;
    /** MEJ-02: Gate In, TODO el gate (Venta + Traspaso). Informativo. */
    gateIn?: number;
    /**
     * Porción del gate que SÍ está dentro de osVenta.
     * ⚠️ Restar `gate` en vez de esto da OS Venta negativa cuando el gate es
     * Traspaso (liq 1696, reportada por OPB el 2026-09-01).
     */
    gateVenta?: number;
    /** Porción Venta del Gate Out. Ver `gateVenta`. */
    gateOutVenta?: number;
    /** Porción Venta del Gate In. Ver `gateVenta`. */
    gateInVenta?: number;
    osVenta: number;
    totalVentaNeto: number;
    totalNeto: number;
    osTraspaso: number;
    /** MEJ-02 (D8): osTraspaso sin el gate. Reemplaza a Gate Out/In en la Cabecera. */
    osTraspasoSinGate?: number;
    total: number;
}

@Component({
    selector: 'app-liquidaciones-agencia',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatMenuModule,
        MatChipsModule,
        MatTooltipModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        MatNativeDateModule,
        FormsModule,
        ReactiveFormsModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
    ],
    templateUrl: './liquidaciones-agencia.component.html',
    styleUrl: './liquidaciones-agencia.component.scss',
})
export class LiquidacionesAgenciaComponent implements OnInit, AfterViewInit {
    // Propiedades de clase para los métodos
    // eslint-disable-next-line no-unused-vars
    public mostrarCliente = (cliente: Client): string => {
        return cliente ? cliente.cliente : '';
    };
    // eslint-disable-next-line no-unused-vars
    private mapearLiquidacionesAgencia = (
        liquidaciones: LiquidacionAgenciaService[]
    ): LiquidacionAgenciaLocal[] => {
        this.logger.info(
            `🔄 [MAPEO] Iniciando mapeo de ${liquidaciones.length} liquidaciones`,
            'LiquidacionesAgenciaComponent'
        );

        return liquidaciones.map((liquidacion) => ({
            id: liquidacion.id || 0,
            numeroLiquidacion: (liquidacion.id || 0).toString(),
            // ⚠️ Sin estas dos líneas las columnas "Nro. Factura" y "Nro. Nota de
            // Cobro" del Excel salen vacías aunque la vista y la API sí traigan el
            // dato: el Excel escribe `item.nroFactura`, y `item` es ESTE modelo.
            // La cadena tiene tres saltos (vista → API → este mapeo → Excel) y
            // basta que falte uno para que la columna quede en blanco sin error.
            nroFactura: liquidacion.nroFactura || '',
            nroNotaCobro: liquidacion.nroNotaCobro || '',
            cliente: liquidacion.cliente,
            mesAnio: liquidacion.mesAnio,
            nave: liquidacion.nave || 'N/D',
            navesTooltip: liquidacion.navesTooltip || '',
            tipo: liquidacion.tipo,
            cntr: liquidacion.cntr,
            plts: liquidacion.plts,
            agenciamiento: liquidacion.agenciamiento,
            transporte: liquidacion.transporte,
            gate: liquidacion.gate || 0,
            gateOut: liquidacion.gateOut || 0,
            gateIn: liquidacion.gateIn || 0,
            gateVenta: liquidacion.gateVenta || 0,
            gateOutVenta: liquidacion.gateOutVenta || 0,
            gateInVenta: liquidacion.gateInVenta || 0,
            osVenta: liquidacion.osVenta,
            totalVentaNeto: liquidacion.totalVentaNeto || 0,
            totalNeto: liquidacion.totalNeto || 0,
            osTraspaso: liquidacion.osTraspaso,
            total: liquidacion.total,
            estado: liquidacion.estado,
            agente: liquidacion.agente,
            fechaCreacion: liquidacion.fechaLiquidacion || '',
        }));
    };
    // eslint-disable-next-line no-unused-vars
    public formatearMesAnio = (mesAnio: string): string => {
        if (!mesAnio) return '';

        this.logger.debug(
            `🔥 [AGENCIA] formatearMesAnio llamado con: "${mesAnio}"`
        );

        // Formato 1: MM-YYYY (ej: 12-2025)
        const partesGuion = mesAnio.split('-');
        if (partesGuion.length >= 2) {
            const mesNum = parseInt(partesGuion[0], 10);
            const anio = partesGuion[1]?.trim();
            if (!isNaN(mesNum) && mesNum >= 1 && mesNum <= 12 && anio) {
                return `${this.obtenerNombreMesAbreviado(mesNum)}/${anio}`;
            }
        }

        // Formato 2: "septiembre de 2025" o "Septiembre 2025" (desde backend)
        const mesesMap: Record<string, number> = {
            enero: 1,
            febrero: 2,
            marzo: 3,
            abril: 4,
            mayo: 5,
            junio: 6,
            julio: 7,
            agosto: 8,
            septiembre: 9,
            octubre: 10,
            noviembre: 11,
            diciembre: 12,
        };
        const match = mesAnio.match(/^([a-záéíóúñ]+)\s+(?:de\s+)?(\d{4})$/i);
        if (match) {
            const mesNombre = match[1]?.toLowerCase();
            const anioVal = match[2];
            const mesNum = mesNombre ? mesesMap[mesNombre] : 0;
            if (mesNum && anioVal) {
                return `${this.obtenerNombreMesAbreviado(mesNum)}/${anioVal}`;
            }
        }

        return mesAnio;
    };
    public clienteNombreDisplay: string = '';

    // Set para almacenar IDs de liquidaciones seleccionadas (persiste entre páginas)
    selectedLiquidaciones: Set<number> = new Set<number>();

    displayedColumns: string[] = [
        'select',
        'numeroLiquidacion',
        'mesAnio',
        'estado',
        'tipo',
        'cntr',
        'plts',
        'agenciamiento',
        'transporte',
        'osVenta',
        'osTraspaso',
        'total',
        'acciones',
    ];

    // Columnas para la fila de totales (footer)
    displayedColumnsWithTotales: string[] = [];

    // Datos de la tabla
    dataSource: LiquidacionAgenciaLocal[] = [];
    // ✅ CAMBIO: Usar array simple en lugar de MatTableDataSource para paginación del servidor
    filteredDataSource: LiquidacionAgenciaLocal[] = [];

    // Totales de las columnas numéricas (basados en datos filtrados del backend)
    totales: LiquidacionAgenciaTotales = {
        cntr: 0,
        plts: 0,
        agenciamiento: 0,
        transporte: 0,
        gate: 0,
        osVenta: 0,
        totalVentaNeto: 0,
        totalNeto: 0,
        osTraspaso: 0,
        total: 0,
    };

    // Filtros específicos de agencia
    clienteControl = new FormControl<string | Client | null>('');
    clienteSeleccionado: Client | null = null;
    filtroTemporada: string | null = null;
    filtroFechaInicio: Date | null = null;
    filtroFechaFin: Date | null = null;

    // Opciones para filtros
    // Clientes para autocompletar
    //
    // `clientes` se expone como get/set sobre un BehaviorSubject (`clientesSubject`)
    // para arreglar la carrera (race) del autocompletar: antes, `clientesFiltrados`
    // solo se recalculaba cuando el usuario escribía (`clienteControl.valueChanges`).
    // Si `cargarClientes()` (asíncrono) todavía no terminaba cuando el usuario ya
    // había escrito, el filtro corría sobre un arreglo vacío y, al llegar los datos,
    // las opciones NUNCA se refrescaban — mostraba "No se encontraron clientes" hasta
    // que el usuario volvía a teclear. Con `combineLatest` en el setter de
    // `clientesFiltrados` (ver constructor), cualquier asignación a `this.clientes`
    // (acá o en `cargarClientes`) dispara el recálculo solo, sin tocar cada call site.
    private clientesSubject = new BehaviorSubject<Client[]>([]);
    private _clientes: Client[] = [];
    set clientes(value: Client[]) {
        this._clientes = value;
        this.clientesSubject.next(value);
    }
    get clientes(): Client[] {
        return this._clientes;
    }
    clientesFiltrados: Observable<Client[]>;
    isLoadingClientes = false;

    // Usuario logueado y control de rol cliente
    currentUser: User | null = null;
    isClienteRole = false;
    clienteInputDisabled = false;
    /** MEJ-03b: Excel Facturación visible SOLO para admin/operator (spec `agency-billing-export`). */
    isFacturacionVisible = false;

    // Temporadas cargadas desde el backend
    temporadas: TemporadaDtoService[] = [];
    temporadasDisplayOptions: { value: string; label: string }[] = [];

    // Paginación
    pageSize = 10;
    pageSizeOptions = [10, 25, 50, 100];

    // Estado de carga
    isLoading = false;
    loadingMessage = 'Cargando liquidaciones de agencia...';
    isGeneratingExcel = false;
    isGeneratingExcelFusionado = false;
    isGeneratingFacturacion = false;
    totalItems = 0;
    currentPage = 1;
    // Ordenamiento (aplicado a todos los registros en backend)
    orderBy: string = 'fechaLiquidacion';
    orderDirection: 'ASC' | 'DESC' = 'DESC';
    currentSortId: string = '';

    // Filtro de búsqueda local (compatible con patrón VD)
    filtroTexto = '';
    searchTerm = '';

    // ── Filtros del punto A.1 (solicitud de OPB, ago-2026) ──────────────────
    // Se resuelven en el BACKEND (no filtran la página ya cargada): N° Factura y
    // N° Nota de Cobro viven en tblShippingLiquidationCustomerInvoices, que es grano
    // factura — una factura puede agrupar varias liquidaciones y traerlas todas.
    /** 1 = Liquidación, 2 = Reliquidación. Sólo uno a la vez (pedido explícito). */
    filtroTipo: number | null = null;
    filtroNroLiquidacion = '';
    filtroNroFactura = '';
    filtroNroNotaCobro = '';

    /**
     * Punto A.2: el módulo abre mostrando sólo las liquidaciones Finalizadas.
     * El defecto vive acá y no en el backend a propósito: es una regla de
     * PRESENTACIÓN del módulo. En el backend cambiaría la respuesta para todos los
     * consumidores del endpoint sin que lo hayan pedido.
     * El usuario puede cambiarlo o elegir "Todos".
     */
    filtroEstado: number | null = 4;

    /** Estados reales de la base. NO existe "Anulada". */
    readonly opcionesEstado: { id: number; nombre: string }[] = [
        { id: 4, nombre: 'Finalizado' },
        { id: 2, nombre: 'Liquidado' },
        { id: 1, nombre: 'Activo' },
    ];

    /** Opciones del desplegable de Tipo. Los IDs salen de la vista del listado. */
    readonly opcionesTipo: { id: number; nombre: string }[] = [
        { id: 1, nombre: 'Liquidación' },
        { id: 2, nombre: 'Reliquidación' },
    ];

    // API URL para llamadas HTTP
    private readonly apiUrl = environment.apiUrl;
    private temporadasProcesadas = false;
    private initialLoadTriggered = false;
    private shouldTriggerAutoLoad = false;

    // === MÉTODOS DE SELECCIÓN MÚLTIPLE ===

    /**
     * Verifica si una liquidación está seleccionada
     */
    isSelected = (liquidacion: LiquidacionAgenciaLocal): boolean => {
        return this.selectedLiquidaciones.has(liquidacion.id);
    };

    /**
     * Alterna la selección de una liquidación individual
     */
    toggleSelection = (liquidacion: LiquidacionAgenciaLocal): void => {
        if (this.selectedLiquidaciones.has(liquidacion.id)) {
            this.selectedLiquidaciones.delete(liquidacion.id);
        } else {
            this.selectedLiquidaciones.add(liquidacion.id);
        }
    };

    /**
     * Verifica si todos los elementos de la página actual están seleccionados
     */
    isAllPageSelected = (): boolean => {
        if (this.filteredDataSource.length === 0) {
            return false;
        }
        return this.filteredDataSource.every((item) =>
            this.selectedLiquidaciones.has(item.id)
        );
    };

    /**
     * Verifica si al menos un elemento de la página actual está seleccionado (para estado indeterminado)
     */
    isSomePageSelected = (): boolean => {
        if (this.filteredDataSource.length === 0) {
            return false;
        }
        const selectedCount = this.filteredDataSource.filter((item) =>
            this.selectedLiquidaciones.has(item.id)
        ).length;
        return (
            selectedCount > 0 && selectedCount < this.filteredDataSource.length
        );
    };

    /**
     * Alterna la selección de todos los elementos de la página actual
     */
    toggleAllPage = (): void => {
        if (this.isAllPageSelected()) {
            // Deseleccionar todos los de la página actual
            this.filteredDataSource.forEach((item) => {
                this.selectedLiquidaciones.delete(item.id);
            });
        } else {
            // Seleccionar todos los de la página actual
            this.filteredDataSource.forEach((item) => {
                this.selectedLiquidaciones.add(item.id);
            });
        }
    };

    /**
     * Limpia todas las selecciones
     */
    clearAllSelections = (): void => {
        this.selectedLiquidaciones.clear();
    };

    /**
     * Obtiene el conteo de elementos seleccionados
     */
    getSelectedCount = (): number => {
        return this.selectedLiquidaciones.size;
    };

    /**
     * Obtiene los IDs de los elementos seleccionados como array
     */
    getSelectedIds = (): number[] => {
        return Array.from(this.selectedLiquidaciones);
    };

    // === FIN MÉTODOS DE SELECCIÓN MÚLTIPLE ===

    private obtenerNombreMes = (mes: number): string => {
        const meses = [
            'Enero',
            'Febrero',
            'Marzo',
            'Abril',
            'Mayo',
            'Junio',
            'Julio',
            'Agosto',
            'Septiembre',
            'Octubre',
            'Noviembre',
            'Diciembre',
        ];
        return meses[mes - 1] || '';
    };

    private obtenerNombreMesAbreviado = (mes: number): string => {
        const meses = [
            'Ene',
            'Feb',
            'Mar',
            'Abr',
            'May',
            'Jun',
            'Jul',
            'Ago',
            'Sep',
            'Oct',
            'Nov',
            'Dic',
        ];
        return meses[mes - 1] || '';
    };

    private formatearFechaParaAPI = (fecha: Date): string => {
        const year = fecha.getFullYear();
        const month = String(fecha.getMonth() + 1).padStart(2, '0');
        const day = String(fecha.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    /** Sanitiza nombre de hoja Excel (máx 31 caracteres, sin \ / ? * [ ]) */
    private sanitizeSheetName = (name: string): string => {
        const sanitized = name.replace(/[/:*?\\[\]]/g, '-').trim();
        return sanitized.length > 31 ? sanitized.substring(0, 31) : sanitized;
    };

    /**
     * Aplica estilos a una hoja Excel (reutilizado para Excel individual y
     * fusionado). PR-7b: lógica movida a
     * `liquidaciones-agencia-excel-estilos.util.ts` (pura, sin Angular).
     */
    private aplicarEstilosAhojaExcel = async (
        workbook: ExcelJS.Workbook,
        worksheet: ExcelJS.Worksheet,
        titulo: string,
        rows: any[],
        columnHeaders: Array<{ header: string; key: string; width: number }>,
        colors: Record<string, string>,
        logoArrayBuffer: ArrayBuffer | null
    ): Promise<void> =>
        aplicarEstilosAhojaExcelUtil(
            workbook,
            worksheet,
            titulo,
            rows,
            columnHeaders,
            colors,
            logoArrayBuffer,
            () =>
                this.logger.warn(
                    'Error al agregar logo',
                    'LiquidacionesAgenciaComponent'
                )
        );

    // Función auxiliar para capitalizar headers. PR-7b: movida a
    // `liquidaciones-agencia-excel-estilos.util.ts` (pura, sin Angular).
    private capitalizeHeader = (key: string): string =>
        capitalizeHeaderUtil(key);

    // Función auxiliar para determinar ancho de columna. PR-7b: movida a
    // `liquidaciones-agencia-excel-estilos.util.ts` (pura, sin Angular).
    private getColumnWidth = (key: string): number => getColumnWidthUtil(key);

    /**
     * Filtra las columnas para mostrar solo aquellas con servicios presentes.
     * Mantiene columnas básicas identificadoras y filtra dinámicamente las de servicios.
     *
     * PR-7b: lógica movida a `liquidaciones-agencia-excel-estilos.util.ts`
     * (pura, sin Angular).
     *
     * @param allKeys Todas las claves de columnas
     * @param serviciosPresentes Lista de servicios con monto > 0
     * @returns Array filtrado de claves de columnas
     */
    private filtrarColumnasConServiciosPresentes(
        allKeys: string[],
        serviciosPresentes: string[]
    ): string[] {
        return filtrarColumnasConServiciosPresentesUtil(
            allKeys,
            serviciosPresentes
        );
    }

    private aplicarTemporadaPorDefecto(force = false): void {
        if (!this.temporadas.length) {
            if (force) {
                this.filtroTemporada = null;
            }
            return;
        }
        if (!force && this.filtroTemporada) {
            return;
        }
        const ultimaTemporada = this.obtenerTemporadaMasReciente();
        this.filtroTemporada = ultimaTemporada
            ? ultimaTemporada.id.toString()
            : null;
    }

    private obtenerTemporadaMasReciente(): TemporadaDtoService | null {
        if (!this.temporadas.length) {
            return null;
        }
        return this.temporadas.reduce((prev, current) => {
            const prevDate = new Date(
                prev.fechaInicio ?? prev.fechaFin ?? ''
            ).getTime();
            const currentDate = new Date(
                current.fechaInicio ?? current.fechaFin ?? ''
            ).getTime();
            return currentDate >= prevDate ? current : prev;
        });
    }

    private triggerInitialLoadIfNeeded(): void {
        if (this.initialLoadTriggered) {
            return;
        }
        if (!this.shouldTriggerAutoLoad) {
            return;
        }
        if (!this.temporadasProcesadas) {
            return;
        }
        if (this.isClienteRole && !this.clienteSeleccionado) {
            return;
        }
        this.initialLoadTriggered = true;
        this.logger.info(
            '📋 Cargando datos iniciales con temporada por defecto',
            'LiquidacionesAgenciaComponent'
        );
        this.cargarDatos();
    }

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    /**
     * Estado actual de los controles de filtro, en la forma que espera el util.
     *
     * Existe para que el listado y la exportación lean EXACTAMENTE lo mismo. Si
     * mañana se agrega un filtro a la pantalla, se agrega acá una sola vez y las dos
     * superficies lo reciben — que es justo lo que no pasó con el punto A.1 y
     * terminó en el Excel vacío de la liquidación 1623.
     */
    private estadoDeFiltros(): EstadoFiltrosLiquidaciones {
        return {
            clienteId: this.clienteSeleccionado?.clienteId ?? null,
            temporada: this.filtroTemporada,
            fechaDesde: this.filtroFechaInicio
                ? this.formatearFechaParaAPI(this.filtroFechaInicio)
                : undefined,
            fechaHasta: this.filtroFechaFin
                ? this.formatearFechaParaAPI(this.filtroFechaFin)
                : undefined,
            tipoId: this.filtroTipo,
            nroLiquidacion: this.filtroNroLiquidacion,
            nroFactura: this.filtroNroFactura,
            nroNotaCobro: this.filtroNroNotaCobro,
            estadoId: this.filtroEstado,
            orderBy: this.orderBy,
            orderDirection: this.orderDirection,
        };
    }

    constructor(
        // eslint-disable-next-line no-unused-vars
        private liquidacionesService: LiquidacionesService,
        // eslint-disable-next-line no-unused-vars
        private userService: UserService,
        // eslint-disable-next-line no-unused-vars
        private authService: AuthService,
        // eslint-disable-next-line no-unused-vars
        private clientService: ClientService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private stateService: LiquidacionesStateService,
        // eslint-disable-next-line no-unused-vars
        private navigationService: LiquidacionesNavigationService,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef,
        // eslint-disable-next-line no-unused-vars
        private parameterService: ParameterService,
        // eslint-disable-next-line no-unused-vars
        private exportService: LiquidacionesAgenciaExportService
    ) {
        // Configurar autocompletar de clientes.
        // combineLatest con `clientesSubject` es lo que arregla la carrera: cuando
        // `cargarClientes()` resuelve y asigna `this.clientes`, el setter empuja al
        // subject y este stream se recalcula solo, aunque el usuario no vuelva a
        // escribir (ver comentario junto a `clientesSubject` más arriba).
        this.clientesFiltrados = combineLatest([
            this.clientesSubject,
            this.clienteControl.valueChanges.pipe(
                startWith(this.clienteControl.value)
            ),
        ]).pipe(map(([clientes, value]) => filtrarClientes(clientes, value)));
    }

    // =====================================================
    // MÉTODOS
    // =====================================================

    public onClienteSeleccionado(cliente: Client): void {
        this.logger.info(
            `🔍 [CLIENT] Cliente recibido en onClienteSeleccionado: ${JSON.stringify(
                cliente
            )}`,
            'LiquidacionesAgenciaComponent'
        );

        this.clienteSeleccionado = cliente;
        this.logger.info(
            `🔍 [CLIENT] Cliente seleccionado: ${cliente.cliente} (ID: ${cliente.clienteId})`,
            'LiquidacionesAgenciaComponent'
        );
        // No aplicar filtros automáticamente; esperar clic en Buscar
    }

    public limpiarCliente(): void {
        // Si el usuario tiene rol cliente, no permitir limpiar el cliente
        if (this.isClienteRole) {
            this.logger.info(
                'Usuario con rol cliente - no se permite limpiar cliente',
                'LiquidacionesAgenciaComponent'
            );
            return;
        }

        this.clienteControl.setValue(null);
        this.clienteSeleccionado = null;
        this.logger.info(
            'Cliente limpiado (sin aplicar aún)',
            'LiquidacionesAgenciaComponent'
        );
        this.cdr.detectChanges();
    }

    public cargarDatos(): void {
        this.logger.info(
            '🚀 [COMPONENT] Iniciando carga de datos de liquidaciones agencia',
            'LiquidacionesAgenciaComponent'
        );

        // Mostrar estado de cliente seleccionado
        this.logger.info(
            `🔍 [FILTER] Cliente seleccionado: ${
                this.clienteSeleccionado?.cliente || 'NINGUNO'
            } (ID: ${this.clienteSeleccionado?.clienteId || 'NINGUNO'})`,
            'LiquidacionesAgenciaComponent'
        );
        this.logger.info(
            `🔍 [FILTER] clienteId en filtros: ${
                this.clienteSeleccionado?.clienteId || 'UNDEFINED'
            }`,
            'LiquidacionesAgenciaComponent'
        );

        // ⚠️ Los filtros se arman en `construirFiltrosLiquidaciones`, NO acá.
        // La exportación usa la MISMA función: cuando cada uno tenía su copia,
        // la del Excel se quedó sin los filtros del punto A.1 y generó archivos
        // vacíos (liq 1623, reportado por OPB el 2026-09-04).
        const filters: LiquidacionAgenciaFilter = construirFiltrosLiquidaciones(
            this.estadoDeFiltros(),
            {
                page: this.currentPage,
                limit: this.pageSize,
            }
        );

        this.logger.info(
            `📋 [COMPONENT] Filtros enviados al servicio: ${JSON.stringify(
                filters
            )}`,
            'LiquidacionesAgenciaComponent'
        );

        this.isLoading = true;
        this.loadingMessage = 'Cargando liquidaciones de agencia...';

        this.liquidacionesService
            .getLiquidacionesAgencia(filters)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar liquidaciones',
                        error as Error,
                        'LiquidacionesAgenciaComponent'
                    );
                    this.snackBar.open(
                        'Error al cargar liquidaciones',
                        'Cerrar',
                        {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        }
                    );
                    return of({
                        items: [],
                        total: 0,
                        page: 1,
                        limit: 10,
                        resumen: undefined,
                    });
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((response) => {
                // Mapear datos a formato local
                this.dataSource = this.mapearLiquidacionesAgencia(
                    response.items || []
                );
                this.totalItems = response.total || 0;

                // Usar totales del nodo resumen del payload del servicio en lugar de calcular localmente
                if (response?.resumen) {
                    this.totales = {
                        cntr: response.resumen.totalCNTR || 0,
                        plts: response.resumen.totalPLTS || 0,
                        agenciamiento: response.resumen.totalAgenciamiento || 0,
                        transporte: response.resumen.totalTransporte || 0,
                        gate: response.resumen.totalGate || 0,
                        osVenta: response.resumen.totalOSVenta || 0,
                        totalVentaNeto: response.resumen.totalVentaNeto || 0,
                        totalNeto: response.resumen.totalNeto || 0,
                        osTraspaso: response.resumen.totalOSTraspaso || 0,
                        total: response.resumen.totalGeneral || 0,
                    };
                } else {
                    // Fallback: calcular si no vienen en el payload
                    this.calcularTotales(this.dataSource);
                }

                // Aplicar filtro de texto si existe
                this.filtrarDatos();

                // Configurar sorting y paginación
                this.configurarSortingYPaginacion();

                this.logger.info(
                    `✅ [COMPONENT] Datos cargados: ${this.dataSource.length} items, total: ${this.totalItems}`,
                    'LiquidacionesAgenciaComponent'
                );
            });
    }

    private calcularTotales(datos: LiquidacionAgenciaLocal[]): void {
        // Reiniciar totales
        this.totales = {
            cntr: 0,
            plts: 0,
            agenciamiento: 0,
            transporte: 0,
            gate: 0,
            osVenta: 0,
            totalVentaNeto: 0,
            totalNeto: 0,
            osTraspaso: 0,
            total: 0,
        };

        // Calcular totales
        datos.forEach((item) => {
            this.totales.cntr += item.cntr;
            this.totales.plts += item.plts;
            this.totales.agenciamiento += item.agenciamiento;
            this.totales.transporte += item.transporte;
            this.totales.gate += item.gate;
            this.totales.osVenta += item.osVenta;
            this.totales.totalVentaNeto += item.totalVentaNeto;
            this.totales.totalNeto += item.totalNeto;
            this.totales.osTraspaso += item.osTraspaso;
            this.totales.total += item.total;
        });
    }

    public cargarTemporadas(): void {
        this.logger.info(
            'Cargando temporadas desde el backend',
            'LiquidacionesAgenciaComponent'
        );
        this.logger.info(
            'Obteniendo temporadas desde el backend',
            'LiquidacionesService'
        );

        this.liquidacionesService
            .getTemporadas()
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar temporadas',
                        error as Error,
                        'LiquidacionesAgenciaComponent'
                    );
                    this.snackBar.open('Error al cargar temporadas', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of([]);
                })
            )
            .subscribe((temporadas) => {
                this.procesarTemporadas(temporadas as TemporadaDtoService[]);
                this.logger.info(
                    `✅ Temporadas cargadas: ${this.temporadas.length}`,
                    'LiquidacionesAgenciaComponent'
                );
            });
    }

    private formatearTemporada(temporada: TemporadaDtoService): string {
        const fechaInicio = new Date(temporada.fechaInicio);
        const fechaFin = new Date(temporada.fechaFin);

        const anoInicio = fechaInicio.getFullYear();
        const anoFin = fechaFin.getFullYear();

        if (anoInicio === anoFin) {
            return anoInicio.toString();
        } else {
            return `${anoInicio}-${anoFin}`;
        }
    }

    private filtrarDatos(): void {
        if (!this.filtroTexto || this.filtroTexto.trim() === '') {
            // Si no hay término de búsqueda, mostrar todos los datos
            this.filteredDataSource = [...this.dataSource];
        } else {
            const termino = this.filtroTexto.toLowerCase().trim();

            // Filtrar datos localmente
            this.filteredDataSource = this.dataSource.filter((item) => {
                // Buscar en todos los campos relevantes
                return (
                    item.numeroLiquidacion.toLowerCase().includes(termino) ||
                    item.cliente.toLowerCase().includes(termino) ||
                    item.mesAnio.toLowerCase().includes(termino) ||
                    item.nave.toLowerCase().includes(termino) ||
                    item.estado.toLowerCase().includes(termino) ||
                    item.agente.toLowerCase().includes(termino) ||
                    item.tipo.toLowerCase().includes(termino)
                );
            });
        }

        // Los totales vienen del backend (nodo resumen) y no deben recalcularse con el filtro de texto local
        // El filtro de texto solo afecta la visualización, no los totales agregados
    }

    public onSearchChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.searchTerm = target.value;
        this.filtrarDatos();
    }

    public limpiarBusqueda(): void {
        this.searchTerm = '';
        this.filtroTexto = '';
        this.filtrarDatos();
    }

    public aplicarFiltros(): void {
        this.filtrarDatos();
    }

    public aplicarFiltrosBackend(): void {
        // Resetear a la primera página cuando se aplican filtros del backend
        this.currentPage = 1;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        // ERR-01: limpiar selecciones al cambiar filtros para evitar incluir
        // liquidaciones de páginas anteriores en el Excel fusionado
        this.selectedLiquidaciones.clear();
        this.cargarDatos();
    }

    public applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        this.logger.debug(
            `🔍 [FILTRO RAPIDO] Valor ingresado: "${filterValue}"`
        );
        this.filtroTexto = filterValue;
        this.searchTerm = filterValue; // Mantener sincronizado

        // Aplicar filtro
        this.filtrarDatos();

        this.logger.debug(
            `🔍 [FILTRO RAPIDO] dataSource.length: ${this.dataSource.length}`
        );
        this.aplicarFiltros();
    }

    public onPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.currentPage = event.pageIndex + 1;
        this.pageSize = event.pageSize;

        // Persistir filtros + paginación juntos para NO perder filtros al paginar
        this.stateService.updateAgenciaState({
            filters: {
                clienteId: this.clienteSeleccionado?.clienteId || null,
                temporada: this.filtroTemporada
                    ? parseInt(this.filtroTemporada)
                    : undefined,
                fechaInicio: this.filtroFechaInicio,
                fechaFin: this.filtroFechaFin,
                filtroTexto: this.filtroTexto,
            },
            pagination: { page: this.currentPage, pageSize: this.pageSize },
        });

        // Sincronizar URL con el estado actual (incluye filtros)
        this.navigationService.applyAgenciaFilters();

        // Recargar datos respetando los filtros actuales
        this.cargarDatos();
    }

    public limpiarFiltros(): void {
        // Limpiar selección de registros
        this.clearAllSelections();

        // Solo limpiar cliente si no es rol cliente
        if (!this.isClienteRole) {
            this.limpiarCliente();
        }
        this.aplicarTemporadaPorDefecto(true);
        this.filtroFechaInicio = null;
        this.filtroFechaFin = null;
        this.filtroTexto = '';
        this.searchTerm = '';
        // Punto A.1: si no se limpian acá, quedan activos en silencio y el
        // listado sigue devolviendo el subconjunto filtrado tras "Limpiar".
        this.filtroTipo = null;
        this.filtroNroLiquidacion = '';
        this.filtroNroFactura = '';
        this.filtroNroNotaCobro = '';
        // A.2: "Limpiar" vuelve al DEFECTO del módulo (Finalizado), no a "Todos".
        this.filtroEstado = 4;

        // Reiniciar a primera página siempre
        this.currentPage = 1;

        if (this.isClienteRole) {
            // Preservar clienteId en el estado y limpiar el resto
            const rawClienteId =
                this.currentUser?.clientId ??
                this.clienteSeleccionado?.clienteId ??
                null;
            const clienteId: number | null =
                rawClienteId !== null ? Number(rawClienteId) : null;
            this.stateService.updateAgenciaState({
                filters: {
                    clienteId,
                    temporada: this.filtroTemporada
                        ? parseInt(this.filtroTemporada)
                        : null,
                    fechaInicio: null,
                    fechaFin: null,
                    filtroTexto: '',
                },
                pagination: {
                    page: this.currentPage,
                    pageSize: this.pageSize,
                },
            });
            this.navigationService.applyAgenciaFilters();
        } else {
            // Actualizar estado con los filtros por defecto (incluyendo temporada seleccionada)
            this.actualizarEstadoFiltros();
        }

        // Cargar datos según el estado resultante
        this.cargarDatos();
    }

    public verDetalle(liquidacion: LiquidacionAgenciaLocal): void {
        this.logger.info(
            `Viendo detalle de liquidación: ${liquidacion.numeroLiquidacion}`,
            'LiquidacionesAgenciaComponent'
        );

        // IMPORTANTE: Guardar el estado actual antes de navegar al detalle
        this.actualizarEstadoFiltros();

        // Abrir detalle en nueva pestaña manteniendo filtros en la URL
        this.navigationService.openAgenciaDetailInNewTab(liquidacion.id);
    }

    // PR-7c: la orquestación (versión v1/v2, fetch, armado del workbook,
    // descarga) vive en `LiquidacionesAgenciaExportService`. Acá solo
    // queda lo que es del COMPONENTE: la flag de UI y traducir cada aviso
    // del servicio a un snackbar de Angular Material.
    public generarExcel(liquidacion: LiquidacionAgenciaLocal): void {
        this.isGeneratingExcel = true;
        this.exportService
            .generarExcelIndividual(liquidacion, (mensaje) =>
                this.snackBar.open(mensaje.texto, 'Cerrar', {
                    duration: mensaje.duracionMs,
                    panelClass: mensaje.panelClass,
                })
            )
            .pipe(
                finalize(() => {
                    this.isGeneratingExcel = false;
                })
            )
            .subscribe();
    }

    // MEJ-03b (D11): Excel Facturación. La hoja completa vive en
    // `liquidacion-agencia-facturacion-excel.util.ts`; acá sólo wiring
    // (armar el body, pedir al backend, descargar el blob).
    public async exportarExcelFacturacion(): Promise<void> {
        const idsSeleccionados = this.getSelectedIds();
        const filtrosActuales: LiquidacionAgenciaFilter =
            construirFiltrosLiquidaciones(this.estadoDeFiltros(), {
                page: 1,
                limit: 1,
            });
        const body = buildFacturacionRequestBody(
            idsSeleccionados,
            filtrosActuales
        );

        this.isGeneratingFacturacion = true;
        this.liquidacionesService
            .facturarExcel(body)
            .pipe(
                catchError((error) => {
                    // El backend rechaza (400) sin truncar en silencio
                    // cuando el conjunto filtrado supera FACTURACION_MAX_ROWS
                    // (2000) — ver `LiquidacionAgenciaFacturacionService`.
                    const esLimiteExcedido =
                        error?.status === 400 &&
                        typeof error?.error?.message === 'string' &&
                        error.error.message.includes('máximo');
                    this.logger.error(
                        'Error al exportar el Excel Facturación',
                        error as Error,
                        'LiquidacionesAgenciaComponent'
                    );
                    this.snackBar.open(
                        esLimiteExcedido
                            ? 'Hay más de 2.000 liquidaciones en el filtro. Acota el filtro o selecciona menos.'
                            : 'Error al exportar el Excel Facturación',
                        'Cerrar',
                        { duration: 6000, panelClass: ['error-snackbar'] }
                    );
                    return of(null);
                }),
                finalize(() => {
                    this.isGeneratingFacturacion = false;
                })
            )
            .subscribe((response) => {
                if (!response) return; // ya se avisó en catchError
                if (response.rows.length === 0) {
                    this.snackBar.open(
                        'No hay liquidaciones para exportar con el filtro o selección actual',
                        'Cerrar',
                        {
                            duration: 4000,
                            panelClass: ['warning-snackbar'],
                        }
                    );
                    return;
                }
                const workbook = buildFacturacionWorkbook(response.rows);
                workbook.xlsx.writeBuffer().then((buffer) => {
                    const blob = new Blob([buffer], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = buildFacturacionFilename();
                    link.click();
                    window.URL.revokeObjectURL(url);
                    this.snackBar.open(
                        'Excel Facturación exportado exitosamente',
                        'Cerrar',
                        {
                            duration: 3000,
                            panelClass: ['success-snackbar'],
                        }
                    );
                });
            });
    }

    // PR-7c: orquestación movida a
    // `LiquidacionesAgenciaExportService.generarExcelFusionado`. El guard
    // de "selección vacía" se queda acá porque antes NUNCA activaba la
    // flag `isGeneratingExcelFusionado`.
    public generarExcelFusionado(): void {
        const ids = this.getSelectedIds();
        if (ids.length === 0) {
            this.snackBar.open(
                'Seleccione al menos una liquidación',
                'Cerrar',
                { duration: 3000, panelClass: ['warning-snackbar'] }
            );
            return;
        }
        this.isGeneratingExcelFusionado = true;
        this.exportService
            .generarExcelFusionado(ids, (mensaje) =>
                this.snackBar.open(mensaje.texto, 'Cerrar', {
                    duration: mensaje.duracionMs,
                    panelClass: mensaje.panelClass,
                })
            )
            .pipe(
                finalize(() => {
                    this.isGeneratingExcelFusionado = false;
                })
            )
            .subscribe();
    }

    public generarPdfAgencia(liquidacion: LiquidacionAgenciaLocal): void {
        this.logger.info(
            `Generando PDF para liquidación: ${liquidacion.numeroLiquidacion}`,
            'LiquidacionesAgenciaComponent'
        );

        this.isLoading = true;
        this.loadingMessage = 'Generando PDF...';

        this.liquidacionesService
            .generarPdfLiquidacionAgencia(liquidacion.id)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (blob: Blob) => {
                    // Crear URL del blob
                    const url = window.URL.createObjectURL(blob);

                    // Crear link temporal y hacer click
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `liquidacion_${liquidacion.numeroLiquidacion}.pdf`;
                    document.body.appendChild(link);
                    link.click();

                    // Limpiar
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    this.logger.info(
                        `✅ PDF descargado para liquidación: ${liquidacion.numeroLiquidacion}`,
                        'LiquidacionesAgenciaComponent'
                    );

                    this.snackBar.open('PDF generado exitosamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar'],
                    });
                },
                error: (error) => {
                    this.logger.error(
                        'Error al obtener PDF del servidor',
                        error as Error,
                        'LiquidacionesAgenciaComponent'
                    );
                    this.snackBar.open('Error al generar PDF', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                },
            });
    }

    private configurarSortingYPaginacion(): void {
        if (!this.sort) {
            return;
        }

        // Suscribirse al cambio de orden del header
        this.sort.sortChange.subscribe(({ active, direction }) => {
            // Mapear id de columna a nombre de campo del backend
            const mapped = this.mapSortIdToOrderBy(active);
            this.orderBy = mapped;
            this.orderDirection =
                direction && direction.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';

            // Resetear a primera página y recargar desde backend con orden aplicado a todos los registros
            this.currentPage = 1;
            if (this.paginator) {
                this.paginator.firstPage();
            }
            this.cargarDatos();
        });
    }

    // Mapeo de ids de columnas de la tabla a campos de ordenamiento del backend
    private mapSortIdToOrderBy(id: string): string {
        const mapping: Record<string, string> = {
            numeroLiquidacion: 'numeroLiquidacion',
            mesAnio: 'mesAnio',
            cliente: 'cliente',
            estado: 'estado',
            tipo: 'tipo',
            cntr: 'cntr',
            plts: 'plts',
            agenciamiento: 'agenciamiento',
            transporte: 'transporte',
            osVenta: 'osVenta',
            osTraspaso: 'osTraspaso',
            total: 'total',
        };
        return mapping[id] || 'fechaLiquidacion';
    }

    public onFechaInicioChange(): void {
        this.logger.debug(
            '📅 Fecha inicio cambiada (sin aplicar aún)',
            'LiquidacionesAgenciaComponent'
        );
    }

    public onFechaFinChange(): void {
        this.logger.debug(
            '📅 Fecha fin cambiada (sin aplicar aún)',
            'LiquidacionesAgenciaComponent'
        );
    }

    public onTemporadaChange(): void {
        this.logger.debug(
            '📅 Temporada cambiada (sin aplicar aún)',
            'LiquidacionesAgenciaComponent'
        );
    }

    // PR-7d: orquestación movida a
    // `LiquidacionesAgenciaExportService.exportarExcelListado`. Se le
    // pasan los filtros/selección actuales y los dos callbacks puros que
    // el componente ya tenía (`mapearLiquidacionesAgencia`,
    // `formatearMesAnio`) en vez de que el servicio lea `this`.
    public async exportarExcel(): Promise<void> {
        this.isGeneratingExcel = true;
        try {
            await this.exportService.exportarExcelListado(
                this.estadoDeFiltros(),
                this.getSelectedIds(),
                this.mapearLiquidacionesAgencia,
                this.formatearMesAnio,
                (mensaje) =>
                    this.snackBar.open(mensaje.texto, 'Cerrar', {
                        duration: mensaje.duracionMs,
                        panelClass: mensaje.panelClass,
                    })
            );
        } finally {
            this.isGeneratingExcel = false;
        }
    }

    public formatearNumero(valor: number): string {
        if (valor === null || valor === undefined) return '0';
        return valor.toLocaleString('es-CL');
    }

    public formatearMoneda(valor: number): string {
        if (valor === null || valor === undefined) return '$ 0';
        return `$ ${valor.toLocaleString('es-CL')}`;
    }

    // =====================================================
    // LIFECYCLE METHODS
    // =====================================================

    ngOnInit(): void {
        this.inicializarUsuario();
        this.cargarTemporadas();
        this.cargarClientes();

        // Intentar cargar estado desde URL primero, luego desde storage
        const stateLoaded = this.navigationService.loadAgenciaStateFromUrl(
            this.route
        );

        if (stateLoaded) {
            this.logger.info(
                '✅ Estado cargado desde URL',
                'LiquidacionesAgenciaComponent'
            );
        } else {
            this.logger.info(
                'ℹ️ No hay estado en URL, usando estado por defecto',
                'LiquidacionesAgenciaComponent'
            );
        }

        this.shouldTriggerAutoLoad = !this.isClienteRole && !stateLoaded;

        // SIEMPRE suscribirse a cambios de estado
        this.suscribirseAEstado();

        if (this.shouldTriggerAutoLoad) {
            this.logger.info(
                '📋 Preparando carga inicial automática (sin estado desde URL)',
                'LiquidacionesAgenciaComponent'
            );
        } else if (stateLoaded) {
            this.logger.info(
                '⏳ Estado cargado desde URL, aplicarEstadoEnComponente decidirá si cargar datos',
                'LiquidacionesAgenciaComponent'
            );
        }

        // Fechas por defecto si no hay state previo
        if (!this.filtroFechaInicio && !this.filtroFechaFin) {
            const { from, to } = getDefaultDateRange();
            this.filtroFechaInicio = from;
            this.filtroFechaFin = to;
        }

        this.triggerInitialLoadIfNeeded();
    }

    ngAfterViewInit(): void {
        // Configurar sorting y paginación
        this.configurarSortingYPaginacion();

        // Restaurar estado visual del sort si existe estado previo
        if (this.sort && this.currentSortId) {
            this.sort.active = this.currentSortId as any;
            this.sort.direction = this.orderDirection.toLowerCase() as any;
            this.sort.sortChange.emit({
                active: this.sort.active,
                direction: this.sort.direction,
            });
        }

        this.logger.info(
            '🔧 [SORTING] ngAfterViewInit ejecutado',
            'LiquidacionesAgenciaComponent'
        );
    }

    onSortChange(event: { active: string; direction: string }): void {
        // Tercer clic: dirección vacía → limpiar sort visual y volver a orden por defecto
        if (!event.direction) {
            this.orderBy = 'fechaLiquidacion';
            this.orderDirection = 'DESC';
            if (this.sort) {
                // Limpiar estado visual del sort (estado neutro)
                this.sort.active = '' as any;
                this.sort.direction = '' as any;
            }
            this.currentSortId = '';
        } else {
            const mapped = this.mapSortIdToOrderBy(event.active);
            this.orderBy = mapped;
            this.orderDirection =
                event.direction.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
            this.currentSortId = event.active;
        }

        this.currentPage = 1;
        if (this.paginator) {
            this.paginator.firstPage();
        }
        this.cargarDatos();
    }

    /**
     * Suscribirse a cambios de estado y aplicar automáticamente
     */
    private suscribirseAEstado(): void {
        this.stateService.getAgenciaState().subscribe((state) => {
            this.aplicarEstadoEnComponente(state);
        });
    }

    /**
     * Aplicar el estado recibido a las propiedades del componente
     */
    private aplicarEstadoEnComponente(state: LiquidacionesState): void {
        this.logger.debug(
            '🔄 Aplicando estado en componente Agencia',
            'LiquidacionesAgenciaComponent'
        );

        // Aplicar filtros básicos primero
        // IMPORTANTE: Convertir temporada a string para que coincida con el template
        this.filtroTemporada = state.filters.temporada
            ? state.filters.temporada.toString()
            : null;
        this.filtroFechaInicio = state.filters.fechaInicio || null;
        this.filtroFechaFin = state.filters.fechaFin || null;
        this.filtroTexto = state.filters.filtroTexto || '';

        // IMPORTANTE: Aplicar los valores a los controles del formulario para que se muestren visualmente
        if (this.filtroTemporada) {
            // Buscar la temporada en las opciones disponibles
            const temporadaOption = this.temporadasDisplayOptions.find(
                (opt) => opt.value === this.filtroTemporada
            );
            if (temporadaOption) {
                // El filtro ya está aplicado, solo necesitamos sincronizar la UI
                this.logger.debug(
                    `✅ Temporada aplicada visualmente: ${temporadaOption.label}`,
                    'LiquidacionesAgenciaComponent'
                );
            }
        }

        // Aplicar paginación
        this.currentPage = state.pagination.page;
        this.pageSize = state.pagination.pageSize;

        // Actualizar paginador visual si existe
        if (this.paginator) {
            this.paginator.pageIndex = this.currentPage - 1; // MatPaginator usa índice 0-based
            this.paginator.pageSize = this.pageSize;
        }

        // IMPORTANTE: Si hay filtros principales (backend), recargar datos
        const hasFiltrosPrincipales =
            state.filters.clienteId ||
            state.filters.temporada ||
            state.filters.fechaInicio ||
            state.filters.fechaFin;

        // Manejar cliente de forma asíncrona si es necesario
        if (state.filters.clienteId) {
            // Si no hay clientes cargados aún, esperar a que se carguen
            if (this.clientes.length === 0) {
                this.logger.info(
                    '🔄 [aplicarEstadoEnComponente] Clientes no cargados, cargando primero...',
                    'LiquidacionesAgenciaComponent'
                );
                this.cargarClientes().then(() => {
                    const cliente = this.clientes.find(
                        (c: Client) => c.clienteId === state.filters.clienteId
                    );
                    if (cliente) {
                        this.clienteSeleccionado = cliente;
                        // Para rol cliente, mostrar string en input readonly; para otros roles, mantener objeto para autocomplete
                        this.clienteControl.setValue(
                            this.isClienteRole ? cliente.cliente : cliente
                        );
                        if (this.isClienteRole) {
                            this.clienteControl.disable();
                        }
                        this.logger.info(
                            `✅ Cliente aplicado desde estado: ${cliente.cliente}`,
                            'LiquidacionesAgenciaComponent'
                        );

                        // IMPORTANTE: Recargar datos después de aplicar el cliente
                        if (hasFiltrosPrincipales) {
                            this.logger.info(
                                `🔄 [aplicarEstadoEnComponente] Recargando datos después de aplicar cliente`,
                                'LiquidacionesAgenciaComponent'
                            );
                            this.cargarDatos();
                        }
                    }
                    // Forzar detección de cambios después de aplicar cliente
                    this.cdr.detectChanges();
                });
            } else {
                const cliente = this.clientes.find(
                    (c: Client) => c.clienteId === state.filters.clienteId
                );
                if (cliente) {
                    this.clienteSeleccionado = cliente;
                    this.clienteControl.setValue(
                        this.isClienteRole ? cliente.cliente : cliente
                    );
                    if (this.isClienteRole) {
                        this.clienteControl.disable();
                    }
                    this.logger.info(
                        `✅ Cliente aplicado desde estado: ${cliente.cliente}`,
                        'LiquidacionesAgenciaComponent'
                    );
                }

                // Recargar datos si hay filtros principales
                if (hasFiltrosPrincipales) {
                    this.logger.info(
                        `🔄 [aplicarEstadoEnComponente] Hay filtros principales, recargando datos del backend`,
                        'LiquidacionesAgenciaComponent'
                    );
                    this.cargarDatos();
                }
                // Forzar detección de cambios después de aplicar cliente
                this.cdr.detectChanges();
            }
        } else {
            // No hay clienteId, recargar datos directamente si hay otros filtros
            if (hasFiltrosPrincipales) {
                this.logger.info(
                    `🔄 [aplicarEstadoEnComponente] Hay filtros principales (sin cliente), recargando datos del backend`,
                    'LiquidacionesAgenciaComponent'
                );
                this.cargarDatos();
            } else {
                // IMPORTANTE: Si no hay filtros principales pero estamos restaurando desde URL,
                // cargar datos para mostrar el listado completo
                this.logger.info(
                    `🔄 [aplicarEstadoEnComponente] Sin filtros principales, cargando datos del backend (restauración desde URL)`,
                    'LiquidacionesAgenciaComponent'
                );
                this.cargarDatos();
            }
            // Forzar detección de cambios
            this.cdr.detectChanges();
        }

        // IMPORTANTE: Aplicar el filtro de texto a la tabla si existe
        if (this.filtroTexto) {
            this.searchTerm = this.filtroTexto; // Sincronizar con searchTerm
            // Solo aplicar filtro si ya hay datos cargados
            if (this.dataSource && this.dataSource.length > 0) {
                this.filtrarDatos();
            }
        }

        this.logger.info(
            `✅ [aplicarEstadoEnComponente] Estado aplicado: página ${
                this.currentPage
            }, filtro texto: "${this.filtroTexto}", dataSource: ${
                this.dataSource?.length || 0
            } items`,
            'LiquidacionesAgenciaComponent'
        );
    }

    /**
     * Actualizar el estado con los filtros actuales
     */
    private actualizarEstadoFiltros(): void {
        const filtrosActuales = {
            clienteId: this.clienteSeleccionado?.clienteId || null,
            temporada: this.filtroTemporada
                ? parseInt(this.filtroTemporada)
                : undefined,
            fechaInicio: this.filtroFechaInicio,
            fechaFin: this.filtroFechaFin,
            filtroTexto: this.filtroTexto,
        };

        this.stateService.updateAgenciaState({
            filters: filtrosActuales,
            pagination: {
                page: this.currentPage,
                pageSize: this.pageSize,
            },
        });

        // Actualizar URL
        this.navigationService.applyAgenciaFilters();
    }

    inicializarUsuario(): void {
        this.currentUser = this.authService.getCurrentUser();

        if (this.currentUser) {
            this.isClienteRole = this.authService.hasRole('client');
            this.isFacturacionVisible = puedeVerBotonFacturacion((role) =>
                this.authService.hasRole(role)
            );
            this.logger.info(
                `Usuario iniciado - Role: ${
                    this.isClienteRole ? 'cliente' : 'otros'
                }, ClientId: ${this.currentUser?.clientId || 'N/A'}`,
                'LiquidacionesAgenciaComponent'
            );

            // Configurar columnas según el rol
            this.configurarColumnasSegunRol();

            if (this.isClienteRole && this.currentUser?.clientId) {
                this.clienteInputDisabled = true;
                this.cargarClienteDelUsuario(Number(this.currentUser.clientId));
            }
        }
    }

    private procesarTemporadas(temporadas: TemporadaDtoService[]): void {
        this.temporadas = Array.isArray(temporadas) ? temporadas.slice() : [];
        this.temporadasDisplayOptions = this.temporadas
            .slice()
            .sort((a, b) => {
                const fechaB = new Date(
                    b.fechaInicio ?? b.fechaFin ?? ''
                ).getTime();
                const fechaA = new Date(
                    a.fechaInicio ?? a.fechaFin ?? ''
                ).getTime();
                return fechaB - fechaA;
            })
            .map((temporada) => ({
                value: temporada.id.toString(),
                label: this.formatearTemporada(temporada),
            }));
        this.aplicarTemporadaPorDefecto();
        this.temporadasProcesadas = true;
        this.triggerInitialLoadIfNeeded();
    }

    /**
     * Configura las columnas de la tabla según el rol del usuario
     * Para usuarios con rol cliente: oculta la columna cliente
     * Para otros roles: muestra la columna cliente
     * Siempre incluye columna de selección (checkbox) al principio
     */
    configurarColumnasSegunRol(): void {
        const columnasBase = [
            'numeroLiquidacion',
            'mesAnio',
            'nave',
            'estado',
            'tipo',
            'cntr',
            'plts',
            'agenciamiento',
            'transporte',
            'osVenta',
            'totalVentaNeto',
            'osTraspaso',
            'total',
            'acciones',
        ];

        if (this.isClienteRole) {
            // Usuario cliente: no mostrar columna cliente, agregar select al inicio
            this.displayedColumns = ['select', ...columnasBase];
            // Configurar columnas de totales (footer)
            this.displayedColumnsWithTotales = [
                'totales-select',
                ...columnasBase.map((col) => `totales-${col}`),
            ];
            this.logger.info(
                'Columnas configuradas para usuario cliente (sin columna cliente)',
                'LiquidacionesAgenciaComponent'
            );
        } else {
            // Otros roles: incluir columna cliente después de numeroLiquidacion, nave después de cliente
            this.displayedColumns = [
                'select',
                'numeroLiquidacion',
                'mesAnio',
                'cliente',
                ...columnasBase.slice(2), // Skip numeroLiquidacion and mesAnio
            ];
            // Configurar columnas de totales (footer) incluyendo cliente
            this.displayedColumnsWithTotales = [
                'totales-select',
                'totales-numeroLiquidacion',
                'totales-mesAnio',
                'totales-cliente',
                ...columnasBase.slice(2).map((col) => `totales-${col}`),
            ];
            this.logger.info(
                'Columnas configuradas para usuario no cliente (con columna cliente)',
                'LiquidacionesAgenciaComponent'
            );
        }
    }

    cargarClienteDelUsuario(clientId: number): void {
        this.logger.info(
            `Cargando cliente específico del usuario: ${clientId}`,
            'LiquidacionesAgenciaComponent'
        );

        this.clientService
            .getClientById(clientId)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar cliente del usuario',
                        error as Error,
                        'LiquidacionesAgenciaComponent'
                    );
                    this.snackBar.open(
                        'Error al cargar información del cliente',
                        'Cerrar',
                        {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        }
                    );
                    return of(null);
                })
            )
            .subscribe((cliente) => {
                if (cliente) {
                    // Crear objeto Client compatible
                    const clienteCompleto: Client = {
                        clienteId: cliente.clienteId,
                        cliente: cliente.cliente,
                    };

                    // Poblar la lista de clientes con el único cliente válido para este usuario
                    // Esto permite que la lógica de `aplicarEstadoEnComponente` funcione correctamente
                    this.clientes = [clienteCompleto];
                    this.clienteSeleccionado = clienteCompleto;

                    this.logger.info(
                        `Cliente del usuario cargado: ${cliente.cliente}. Actualizando estado para aplicar filtros.`,
                        'LiquidacionesAgenciaComponent'
                    );

                    // Actualizar el estado. El flujo reactivo se encargará de:
                    // 1. Llamar a aplicarEstadoEnComponente
                    // 2. Poblar y deshabilitar el input del cliente
                    // 3. Llamar a cargarDatos() con el filtro de cliente
                    this.actualizarEstadoFiltros();
                }
            });
    }

    cargarClientes(): Promise<void> {
        return new Promise((resolve) => {
            // Si es usuario con rol cliente, no cargar todos los clientes
            if (this.isClienteRole) {
                this.logger.info(
                    'Usuario con rol cliente - no cargando lista completa de clientes',
                    'LiquidacionesAgenciaComponent'
                );
                resolve();
                return;
            }

            this.logger.info(
                'Cargando clientes activos desde el backend',
                'LiquidacionesAgenciaComponent'
            );
            this.isLoadingClientes = true;

            this.userService
                .getActiveClients()
                .pipe(
                    catchError((error) => {
                        this.logger.error(
                            'Error al cargar clientes activos',
                            error as Error,
                            'LiquidacionesAgenciaComponent'
                        );
                        this.snackBar.open(
                            'Error al cargar clientes',
                            'Cerrar',
                            {
                                duration: 5000,
                                panelClass: ['error-snackbar'],
                            }
                        );
                        return of({ success: false, data: [] });
                    }),
                    finalize(() => {
                        this.isLoadingClientes = false;
                    })
                )
                .subscribe((response) => {
                    if (response.success && response.data) {
                        this.clientes = response.data;
                        this.logger.info(
                            `Clientes activos cargados: ${this.clientes.length}`,
                            'LiquidacionesAgenciaComponent'
                        );
                    }
                    resolve();
                });
        });
    }
}
